export type AppBranding = {
  productName: string;
  loginTitle: string;
  loginSubtitle: string;
  installPromptTitle: string;
  installPromptDescription: string;
  manifestHref: string;
  faviconSvgHref: string;
  faviconPngHref: string;
  appleTouchIconHref: string;
  themeColor: string;
  backgroundColor: string;
  appleMobileWebAppTitle: string;
};

const defaultBranding: AppBranding = {
  productName: "Project Manager",
  loginTitle: "Project Manager",
  loginSubtitle: "Engineering Consultancy Management",
  installPromptTitle: "Install Project Manager",
  installPromptDescription: "Add this app to your home screen or desktop for quicker access and a cleaner full-screen experience.",
  manifestHref: "/manifest.webmanifest",
  faviconSvgHref: "/favicon.svg",
  faviconPngHref: "/icons/ai-pm-favicon-32.png",
  appleTouchIconHref: "/icons/ai-pm-apple-touch-icon.png",
  themeColor: "#FFF1BE",
  backgroundColor: "#FFF8D9",
  appleMobileWebAppTitle: "Project Manager",
};

const aiPmBranding: AppBranding = {
  productName: "AI PM",
  loginTitle: "AI PM",
  loginSubtitle: "Linfinity company AI workspace",
  installPromptTitle: "Install AI PM",
  installPromptDescription: "Add the AI workspace to your home screen or desktop for faster access and a cleaner full-screen experience.",
  manifestHref: "/manifest-aipm.webmanifest",
  faviconSvgHref: "/icons/ai-pm-icon.svg",
  faviconPngHref: "/icons/ai-pm-favicon-32.png",
  appleTouchIconHref: "/icons/ai-pm-apple-touch-icon.png",
  themeColor: "#FF8FA8",
  backgroundColor: "#FFF4DD",
  appleMobileWebAppTitle: "AI PM",
};

const AI_PM_BRANDING_HOSTNAMES = new Set([
  "aipm.linfinityeng.com",
]);

const AI_PM_BRANDING_HOST_PATTERNS = [
  /^polite-pebble-0bf8e4700\.\d+\.azurestaticapps\.net$/i,
];

function normalizeHostname(hostname?: string | null): string {
  return String(
    hostname ??
    (typeof window !== "undefined" ? window.location.hostname : ""),
  )
    .trim()
    .toLowerCase();
}

export function isAiPmBrandingHost(hostname?: string | null) {
  const resolvedHostname = normalizeHostname(hostname);
  if (!resolvedHostname) {
    return false;
  }

  if (AI_PM_BRANDING_HOSTNAMES.has(resolvedHostname)) {
    return true;
  }

  return AI_PM_BRANDING_HOST_PATTERNS.some((pattern) => pattern.test(resolvedHostname));
}

export function getAppBranding(hostname?: string | null): AppBranding {
  return isAiPmBrandingHost(hostname) ? aiPmBranding : defaultBranding;
}

export function applyAppBrandingToDocument(doc: Document = document, hostname?: string | null) {
  const branding = getAppBranding(hostname);

  doc.title = branding.productName;

  const themeColor = doc.querySelector('meta[name="theme-color"]');
  if (themeColor) {
    themeColor.setAttribute("content", branding.themeColor);
  }

  const applicationName = doc.querySelector('meta[name="application-name"]');
  if (applicationName) {
    applicationName.setAttribute("content", branding.productName);
  }

  const appleTitle = doc.querySelector('meta[name="apple-mobile-web-app-title"]');
  if (appleTitle) {
    appleTitle.setAttribute("content", branding.appleMobileWebAppTitle);
  }

  const manifest = doc.querySelector('link[rel="manifest"]');
  if (manifest) {
    manifest.setAttribute("href", branding.manifestHref);
  }

  const svgIcon = doc.querySelector('link[rel="icon"][type="image/svg+xml"]');
  if (svgIcon) {
    svgIcon.setAttribute("href", branding.faviconSvgHref);
  }

  const pngIcon = doc.querySelector('link[rel="icon"][sizes="32x32"]');
  if (pngIcon) {
    pngIcon.setAttribute("href", branding.faviconPngHref);
  }

  const appleTouchIcon = doc.querySelector('link[rel="apple-touch-icon"]');
  if (appleTouchIcon) {
    appleTouchIcon.setAttribute("href", branding.appleTouchIconHref);
  }
}
